import React, {Props, useEffect, useState} from "react";
import ButtonRounded from "../../../components/ButtonRounded";
import Card from "@material-ui/core/Card";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import {useHistory} from "react-router-dom";
import parseToFloat from "../../../components/texts/parseToFloat";
import {useOverviewContainerStyles} from "../../../common/styles/mandateOverviewContainerStyles";
import { useLocation } from "react-router-dom";
import parseAndFormat from "../../../components/texts/parseAndFormat";
import {GeneralMandateOffroad, MandateSurplusLockedOffroad, MandateSurplusAvailableOffroad,
    ConventionalBiofuelOffroad, VolumeCheckOffroad} from "./preliminarySurplusCalculations";
import { brandColors } from "../../../theme";

const useStyles = useOverviewContainerStyles;

interface DataType {
    generalMandate: GeneralMandateOffroad;
    mandateSurplusLocked: MandateSurplusLockedOffroad;
    mandateSurplusAvailable: MandateSurplusAvailableOffroad;
    conventionalBiofuel: ConventionalBiofuelOffroad;
    volumeCheck: VolumeCheckOffroad;
}

const PrelSurplusOffroadCalculation: React.FC<Props<any>> = () => {
    const classes = useStyles();
    const history = useHistory();
    const [data, setData] = useState<DataType>({
        generalMandate: {},
        mandateSurplusLocked: {},
        mandateSurplusAvailable: {},
        conventionalBiofuel: {},
        volumeCheck: {},
    });
    const location = useLocation<{
        generalMandateReqVol?: any;
        fameAdvBioToBeUsed?: any;
        hvoAdvBioToBeUsed?: any;
        fameAdvBioAboveMandate?: any;
        hvoAdvBioAboveMandate?: any;
        fameSoldVol?: any;
        hvoSoldVol?: any;
        fameAdvSoldVol?: any;
        hvoAdvSoldVol?: any;
        inJointFulfSum?: any;
        outJointFulfSum?: any;
        isOffroad?: boolean;
    }>();
    const state = location.state || {};
    let { generalMandateReqVol, fameAdvBioToBeUsed, hvoAdvBioToBeUsed, fameAdvBioAboveMandate, hvoAdvBioAboveMandate,
        fameSoldVol, hvoSoldVol, fameAdvSoldVol, hvoAdvSoldVol, inJointFulfSum, outJointFulfSum, isOffroad } = state;

    generalMandateReqVol = Math.ceil(generalMandateReqVol);

    const calculateAndSetData = () => {
        const calcMandateReq = generalMandateReqVol;
        const jointFulfillIn = inJointFulfSum === 0 ? "N/A" : inJointFulfSum;
        const x = jointFulfillIn === "N/A" ? 0 : jointFulfillIn;

        let allocation_advanced_fame = 0;
        const diff = calcMandateReq - x;
        if (fameAdvBioToBeUsed >= diff) {
            allocation_advanced_fame = diff;
        } else if (fameAdvBioToBeUsed > 0) {
            allocation_advanced_fame = fameAdvBioToBeUsed;
        } else {
            allocation_advanced_fame = 0;
        }

        let allocation_advanced_hvo_hro = 0;
        const diffHVO = calcMandateReq - x - allocation_advanced_fame;
        if (hvoAdvBioToBeUsed >= diffHVO) {
            allocation_advanced_hvo_hro = diffHVO;
        } else if (hvoAdvBioToBeUsed > 0) {
            allocation_advanced_hvo_hro = hvoAdvBioToBeUsed;
        } else {
            allocation_advanced_hvo_hro = 0;
        }

        const remaining_volume_general_mandate = calcMandateReq - x - allocation_advanced_fame - allocation_advanced_hvo_hro;

        const total_volume_surplus_locked = (outJointFulfSum || 0) + fameAdvBioAboveMandate + hvoAdvBioAboveMandate;
        const advanced_fame_required_by_customers = fameAdvBioAboveMandate == 0 ? "N/A" : fameAdvBioAboveMandate;
        const advanced_hvo_hro_required_by_customers = hvoAdvBioAboveMandate == 0 ? "N/A" : hvoAdvBioAboveMandate;

        let joint_fulfillment_out_advanced_biofuel : any = 0;
        const sum_H10_H11 = allocation_advanced_fame + allocation_advanced_hvo_hro;
        const sum_O46_O48 = fameAdvBioToBeUsed + hvoAdvBioToBeUsed;
        if (outJointFulfSum > 0) {
            if (sum_H10_H11 < sum_O46_O48) {
                if ((sum_O46_O48 - sum_H10_H11) > outJointFulfSum) {
                    joint_fulfillment_out_advanced_biofuel = outJointFulfSum;
                } else {
                    joint_fulfillment_out_advanced_biofuel = sum_O46_O48 - sum_H10_H11;
                }
            } else {
                joint_fulfillment_out_advanced_biofuel = 0;
            }
        } else {
            joint_fulfillment_out_advanced_biofuel = "N/A";
        }

        const sum_H19_to_H21 = fameAdvBioAboveMandate + hvoAdvBioAboveMandate + (joint_fulfillment_out_advanced_biofuel == "N/A" ? 0 : joint_fulfillment_out_advanced_biofuel);
        const volume_diff_mandate_surplus_locked = total_volume_surplus_locked - sum_H19_to_H21;
        console.log("volume_diff_mandate_surplus_locked:", volume_diff_mandate_surplus_locked);
        const total_volume_surplus_available = fameAdvSoldVol + hvoAdvSoldVol - sum_H10_H11 - sum_H19_to_H21;

        const advanced_fame = fameAdvSoldVol - allocation_advanced_fame - (advanced_fame_required_by_customers == "N/A" ? 0 : advanced_fame_required_by_customers);
        const advanced_hvo_hro = hvoAdvSoldVol - allocation_advanced_hvo_hro - (advanced_hvo_hro_required_by_customers == "N/A" ? 0 : advanced_hvo_hro_required_by_customers);
        const volume_adjustment_for_joint_fulfillment_out_advanced_biofuel = joint_fulfillment_out_advanced_biofuel == "N/A" ? "N/A" : (-1) * joint_fulfillment_out_advanced_biofuel;

        const volume_diff_mandate_surplus_available = total_volume_surplus_available - advanced_fame - advanced_hvo_hro - (volume_adjustment_for_joint_fulfillment_out_advanced_biofuel == "N/A" ? 0 : volume_adjustment_for_joint_fulfillment_out_advanced_biofuel);

        const total_volume_conventional_biofuel = fameSoldVol + hvoSoldVol;
        const conventional_fame = fameSoldVol == 0 ? "N/A" : fameSoldVol;
        const conventional_hvo_hro = hvoSoldVol == 0 ? "N/A" : hvoSoldVol;
        const remaining_volume_conventional_biofuel = total_volume_conventional_biofuel - fameSoldVol - hvoSoldVol;

        const joint_fulfillment_in = inJointFulfSum > 0 ? inJointFulfSum - x : "N/A";
        let joint_fulfillment_out : any = (outJointFulfSum || 0) - (joint_fulfillment_out_advanced_biofuel == "N/A" ? 0 : joint_fulfillment_out_advanced_biofuel);
        joint_fulfillment_out = joint_fulfillment_out == 0 ? "N/A" : joint_fulfillment_out;

        const sum_H29_to_H31 = advanced_fame + advanced_hvo_hro + (volume_adjustment_for_joint_fulfillment_out_advanced_biofuel == "N/A" ? 0 : volume_adjustment_for_joint_fulfillment_out_advanced_biofuel);
        const sum_H39_H40 = fameSoldVol + hvoSoldVol;
        const supplied_advanced_biofuel = fameAdvSoldVol + hvoAdvSoldVol - sum_H10_H11 - sum_H19_to_H21 - sum_H29_to_H31;
        const supplied_conventional_biofuel = fameSoldVol + hvoSoldVol - sum_H39_H40;

        const total_volumes = (joint_fulfillment_in == "N/A" ? 0 : joint_fulfillment_in) + (joint_fulfillment_out == "N/A" ? 0 : joint_fulfillment_out) + supplied_advanced_biofuel + supplied_conventional_biofuel;

        let joint_fulfillment_out_comment = "";
        if (joint_fulfillment_out == "N/A") {
            joint_fulfillment_out_comment = "Joint fulfillment OUT is not applicable";
        } else if (joint_fulfillment_out > 0 || joint_fulfillment_out < 0) {
            joint_fulfillment_out_comment = "ERROR Not enough volume for joint fulfillment OUT";
        } else {
            joint_fulfillment_out_comment = "";
        }

        let joint_fulfillment_out_advanced_biofuel_comment = "";
        if (joint_fulfillment_out_advanced_biofuel == "N/A") {
            joint_fulfillment_out_advanced_biofuel_comment = "Joint fulfillment OUT is not applicable";
        } else if (joint_fulfillment_out_advanced_biofuel == 0) {
            joint_fulfillment_out_advanced_biofuel_comment = "ERROR Not enough volume for joint fulfillment OUT";
        } else if (joint_fulfillment_out_advanced_biofuel == outJointFulfSum) {
            joint_fulfillment_out_advanced_biofuel_comment = "";
        } else if (joint_fulfillment_out_advanced_biofuel < outJointFulfSum) {
            joint_fulfillment_out_advanced_biofuel_comment = "ERROR Not enough volume for joint fulfillment OUT";
        } else {
            joint_fulfillment_out_advanced_biofuel_comment = "ERROR Please check the calculation methodology";
        }

        setData({
            generalMandate: {
                calculated_mandate_requirement: generalMandateReqVol,
                joint_fulfillment_in_advanced_biofuel: jointFulfillIn,
                allocation_advanced_fame: allocation_advanced_fame,
                allocation_advanced_hvo_hro: allocation_advanced_hvo_hro,
                remaining_volume_general_mandate: remaining_volume_general_mandate,
                remaining_volume_general_mandate_comment: remaining_volume_general_mandate == 0 ? "OK" : "ERROR Not enough volume to fulfill mandate",
                joint_fulfillment_in_advanced_biofuel_comment: jointFulfillIn == "N/A" ? "Joint fulfillment IN is not applicable" : "",
            },
            mandateSurplusLocked: {
                total_volume_surplus_locked: total_volume_surplus_locked,
                advanced_fame_required_by_customers: advanced_fame_required_by_customers,
                advanced_hvo_hro_required_by_customers: advanced_hvo_hro_required_by_customers,
                joint_fulfillment_out_advanced_biofuel: joint_fulfillment_out_advanced_biofuel,
                volume_diff_mandate_surplus_locked: volume_diff_mandate_surplus_locked,
                volume_diff_mandate_surplus_locked_comment: volume_diff_mandate_surplus_locked == 0 ? "OK" : "ERROR Not enough volume for surplus LOCKED",
                joint_fulfillment_out_advanced_biofuel_comment: joint_fulfillment_out_advanced_biofuel_comment,
            },
            mandateSurplusAvailable: {
                total_volume_surplus_available,
                advanced_fame: advanced_fame,
                advanced_hvo_hro: advanced_hvo_hro,
                volume_adjustment_for_joint_fulfillment_out_advanced_biofuel: volume_adjustment_for_joint_fulfillment_out_advanced_biofuel,
                volume_diff_mandate_surplus_available: volume_diff_mandate_surplus_available,
                volume_diff_mandate_surplus_available_comment: volume_diff_mandate_surplus_available == 0 ? "OK" : "ERROR Not enough volume for surplus AVAILABLE",
            },
            conventionalBiofuel: {
                total_volume_conventional_biofuel: total_volume_conventional_biofuel,
                conventional_fame: conventional_fame,
                conventional_hvo_hro: conventional_hvo_hro,
                remaining_volume_conventional_biofuel: remaining_volume_conventional_biofuel,
                remaining_volume_conventional_biofuel_comment: remaining_volume_conventional_biofuel == 0 ? "OK" : "ERROR Please check the calculation methodology",
            },
            volumeCheck: {
                joint_fulfillment_in: joint_fulfillment_in,
                joint_fulfillment_out: joint_fulfillment_out,
                supplied_advanced_biofuel: supplied_advanced_biofuel,
                supplied_conventional_biofuel: supplied_conventional_biofuel,
                total_volumes: total_volumes,
                total_volumes_comment: total_volumes == 0 ? "OK" : "ERROR Please check the volumes",
                joint_fulfillment_out_comment: joint_fulfillment_out_comment,
                joint_fulfillment_in_comment: joint_fulfillment_in == "N/A" ? "Joint fulfillment IN is not applicable" : "",
            }
        });
    };

    const getCellColor = (cellValue: any) => {
        if (!cellValue) {
            return "#a1aeb7";
        } else if (cellValue == "N/A" || cellValue == 0 || cellValue == "") {
            return "#a1aeb7";
        } else {
            return "#424b5a";
        }
    };

    const getCellColorByComment = (comment: string) => {
        comment = comment.toLowerCase();
        if (comment.includes("error")) {
            return "#d32f2f";
        } else if (comment.includes("not applicable")) {
            return "#a1aeb7";
        } else {
            return "#424b5a";
        }
    };

    const formatDisplayValue = (value: any) => {
        if (value && value === "N/A") {
            return value;
        } else {
            return parseAndFormat(value || 0, 0);
        }
    };

    useEffect(() => {
        calculateAndSetData();
    }, [generalMandateReqVol, fameAdvBioToBeUsed, hvoAdvBioToBeUsed, fameAdvBioAboveMandate, hvoAdvBioAboveMandate,
        fameSoldVol, hvoSoldVol, fameAdvSoldVol, hvoAdvSoldVol, inJointFulfSum, outJointFulfSum]);

    return (
        <div>
            <div className={classes.container}>
                <div>
                    <h1>Preliminary surplus calculation <span style={{ color: brandColors.primary.main }}>{isOffroad ? "OFF-ROAD" : "MARINE"}</span></h1>
                </div>
                <div className={classes.buttonContainer}>
                    <ButtonRounded className={classes.buttonMargin}
                                   fullWidth
                                   variant="contained"
                                   color="primary"
                                   onClick={() => history.goBack()}
                    >
                        Back
                    </ButtonRounded>
                </div>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>General mandate</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Calculated mandate requirement</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.generalMandate.calculated_mandate_requirement)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.joint_fulfillment_in_advanced_biofuel)}}>Joint fulfillment IN advanced biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.joint_fulfillment_in_advanced_biofuel)}}>{formatDisplayValue(data.generalMandate.joint_fulfillment_in_advanced_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.generalMandate.joint_fulfillment_in_advanced_biofuel_comment || ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.allocation_advanced_fame)}}>Allocation advanced FAME</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.allocation_advanced_fame)}}>{formatDisplayValue(data.generalMandate.allocation_advanced_fame)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.allocation_advanced_hvo_hro)}}>Allocation advanced HVO/HRO</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.allocation_advanced_hvo_hro)}}>{formatDisplayValue(data.generalMandate.allocation_advanced_hvo_hro)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColorByComment(data.generalMandate.remaining_volume_general_mandate_comment || "")}}>Remaining volume general mandate</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.generalMandate.remaining_volume_general_mandate_comment || "")}}>{formatDisplayValue(data.generalMandate.remaining_volume_general_mandate)}</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.generalMandate.remaining_volume_general_mandate_comment || "")}}>{data.generalMandate.remaining_volume_general_mandate_comment || ""}</TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Mandate surplus LOCKED</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Total volume surplus LOCKED</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.mandateSurplusLocked.total_volume_surplus_locked)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.advanced_fame_required_by_customers)}}>Advanced FAME required by customers</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.advanced_fame_required_by_customers)}}>{formatDisplayValue(data.mandateSurplusLocked.advanced_fame_required_by_customers)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.advanced_hvo_hro_required_by_customers)}}>Advanced HVO/HRO required by customers</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.advanced_hvo_hro_required_by_customers)}}>{formatDisplayValue(data.mandateSurplusLocked.advanced_hvo_hro_required_by_customers)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel)}}>Joint fulfillment OUT advanced biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel)}}>{formatDisplayValue(data.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel_comment || ""}</TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColorByComment(data.mandateSurplusLocked.volume_diff_mandate_surplus_locked_comment || "")}}>Volume diff. mandate surplus LOCKED</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.mandateSurplusLocked.volume_diff_mandate_surplus_locked_comment|| "")}}>{formatDisplayValue(data.mandateSurplusLocked.volume_diff_mandate_surplus_locked)}</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.mandateSurplusLocked.volume_diff_mandate_surplus_locked_comment || "")}}>{data.mandateSurplusLocked.volume_diff_mandate_surplus_locked_comment || ""}</TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Mandate surplus AVAILABLE</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Total volume surplus AVAILABLE</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.mandateSurplusAvailable.total_volume_surplus_available)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusAvailable.advanced_fame)}}>Advanced Fame</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusAvailable.advanced_fame)}}>{formatDisplayValue(data.mandateSurplusAvailable.advanced_fame)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusAvailable.advanced_hvo_hro)}}>Advanced HVO/HRO</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusAvailable.advanced_hvo_hro)}}>{formatDisplayValue(data.mandateSurplusAvailable.advanced_hvo_hro)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusAvailable.volume_adjustment_for_joint_fulfillment_out_advanced_biofuel)}}>Volume adjustment for joint fulfillment OUT advanced biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusAvailable.volume_adjustment_for_joint_fulfillment_out_advanced_biofuel)}}>{formatDisplayValue(data.mandateSurplusAvailable.volume_adjustment_for_joint_fulfillment_out_advanced_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColorByComment(data.mandateSurplusAvailable.volume_diff_mandate_surplus_available_comment || "")}}>Volume diff. mandate surplus AVAILABLE</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.mandateSurplusAvailable.volume_diff_mandate_surplus_available_comment || "")}}>{formatDisplayValue(data.mandateSurplusAvailable.volume_diff_mandate_surplus_available)}</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.mandateSurplusAvailable.volume_diff_mandate_surplus_available_comment || "")}}>{data.mandateSurplusAvailable.volume_diff_mandate_surplus_available_comment || ""}</TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Conventional biofuel</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Total volume conventional biofuel</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.conventionalBiofuel.total_volume_conventional_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.conventionalBiofuel.conventional_fame)}}>Conventional FAME</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.conventionalBiofuel.conventional_fame)}}>{formatDisplayValue(data.conventionalBiofuel.conventional_fame)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.conventionalBiofuel.conventional_hvo_hro)}}>Conventional HVO/HRO</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.conventionalBiofuel.conventional_hvo_hro)}}>{formatDisplayValue(data.conventionalBiofuel.conventional_hvo_hro)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColorByComment(data.conventionalBiofuel.remaining_volume_conventional_biofuel_comment || "")}}>Remaining volume conventional biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.conventionalBiofuel.remaining_volume_conventional_biofuel_comment || "")}}>{formatDisplayValue(data.conventionalBiofuel.remaining_volume_conventional_biofuel)}</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.conventionalBiofuel.remaining_volume_conventional_biofuel_comment || "")}}>{data.conventionalBiofuel.remaining_volume_conventional_biofuel_comment || ""}</TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Volume Check</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Volume check</TableCell>
                                <TableCell style={{width: "30%"}}>{data.volumeCheck.total_volumes == 0 ? "VOLUME MATCH" : "VOLUME ERROR"}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColorByComment(data.volumeCheck.joint_fulfillment_in_comment || "")}}>Joint fulfillment IN</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.volumeCheck.joint_fulfillment_in_comment || "")}}>
                                    {formatDisplayValue(data.volumeCheck.joint_fulfillment_in)}
                                </TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.volumeCheck.joint_fulfillment_in_comment || "")}}>{data.volumeCheck.joint_fulfillment_in_comment || ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColorByComment(data.volumeCheck.joint_fulfillment_out_comment || "")}}>Joint fulfillment OUT</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.volumeCheck.joint_fulfillment_out_comment || "")}}>
                                    {formatDisplayValue(data.volumeCheck.joint_fulfillment_out)}
                                </TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.volumeCheck.joint_fulfillment_out_comment || "")}}>{data.volumeCheck.joint_fulfillment_out_comment || ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Supplied advanced biofuel</TableCell>
                                <TableCell style={{width: "30%"}}>
                                    {formatDisplayValue(data.volumeCheck.supplied_advanced_biofuel)}
                                </TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Supplied conventional biofuel</TableCell>
                                <TableCell style={{width: "30%"}}>
                                    {formatDisplayValue(data.volumeCheck.supplied_conventional_biofuel)}
                                </TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColorByComment(data.volumeCheck.total_volumes_comment || "")}}>Total volumes</TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.volumeCheck.total_volumes_comment || "")}}>
                                    {formatDisplayValue(data.volumeCheck.total_volumes)}
                                </TableCell>
                                <TableCell style={{width: "30%", color: getCellColorByComment(data.volumeCheck.total_volumes_comment || "")}}>{data.volumeCheck.total_volumes_comment || ""}</TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>
        </div>
    );
};

export default PrelSurplusOffroadCalculation;
